import { TPaymentMethod, TPaymentMethodType } from 'services/api/paymentMethods/types';

export const METHOD_TYPE_LABELS: Record<TPaymentMethodType, string> = {
	card: 'Card',
	applepay: 'Apple Pay',
	googlepay: 'Google Pay',
	bacs_direct_debit: 'Direct Debit',
};

/** Types that take part in the "default" order (cards and wallets; Direct Debit is always used first). */
export const DEFAULT_CAPABLE_TYPES: TPaymentMethodType[] = ['card', 'applepay', 'googlepay'];

export const STATUS_LABELS = {
	ACTIVE: 'Active',
	EXPIRING_SOON: 'Expiring soon',
	EXPIRED: 'Expired',
	PENDING: 'Pending',
	DISABLED: 'Disabled',
	FAILED: 'Failed',
} as const;

export type TMethodStatusLabel = (typeof STATUS_LABELS)[keyof typeof STATUS_LABELS];

export const getMethodStatusLabel = (method: TPaymentMethod): TMethodStatusLabel => {
	switch (method.status) {
		case 'pending':
			return STATUS_LABELS.PENDING;
		case 'disabled':
			return STATUS_LABELS.DISABLED;
		case 'failed':
			return STATUS_LABELS.FAILED;
		default:
			if (method.isExpired) return STATUS_LABELS.EXPIRED;
			if (method.expiringSoon) return STATUS_LABELS.EXPIRING_SOON;
			return STATUS_LABELS.ACTIVE;
	}
};

/** Card/wallet expiry as MM/YY, empty when unknown. */
export const formatExpiry = (
	method: Pick<TPaymentMethod, 'expiryMonth' | 'expiryYear'>,
): string => {
	if (!method.expiryMonth || !method.expiryYear) return '';
	return `${String(method.expiryMonth).padStart(2, '0')}/${String(method.expiryYear % 100).padStart(2, '0')}`;
};

export const CONSENT_TEXT =
	'I authorise EAS to save this payment method with Airwallex and to charge it automatically for my outstanding and future EAS invoices for the selected services, including any bank fee shown in the dashboard. I can remove the payment method or change its services at any time on this page.';

export const RELINK_TEXT =
	'We have changed our payment provider. Please add your card again in Payment methods to keep automatic payments working.';

export const SETUP_COMPLETED_MESSAGE =
	'Payment method added. We will now try to pay your open invoices for the selected services. You will see the result in Transaction history and by e-mail if a payment fails.';
export const SETUP_DUPLICATE_MESSAGE = 'This card is already saved.';
// operator restrictions (api/client-access): shown when the gateway sends no message of its own
export const PAYMENT_METHODS_BLOCKED_TEXT =
	'Adding or changing payment methods is temporarily unavailable.';
export const PAYMENTS_BLOCKED_TEXT = 'Payments from the dashboard are temporarily unavailable.';

export const SETUP_PROCESSING_MESSAGE =
	'Your payment method is still being verified. Please check this page again in a few minutes.';
